import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Heading,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type {
  HrOverviewTeam,
  HrWorkEnvironmentOverview,
} from '../../../../api/hrWorkEnvironment';
import { useRemindToSurveyMutation } from '../../../../hooks/queries/hr/useHrWorkEnvironmentOverviewQuery';
import {
  leadersText,
  surveyDueText,
  surveysDue,
} from '../Overview/hrOverviewView';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import {
  deliveryText,
  reminderErrorText,
} from '../../../WorkEnvironmentSurveyDetail/reminderView';
import classes from '../Overview/Overview.module.css';

/** What the page says for each team reminded, by team */
type Reminded = {
  reminded: Record<number, string>;
  onReminded: (teamIds: number[], text: string) => void;
};

const Sent = ({ text }: { text: string }): ReactElement => (
  <span
    className={`ds-paragraph ${classes.done}`}
    data-size="sm"
    role="status"
  >
    {text}
  </span>
);

const TeamRow = ({
  team,
  reminded,
  onReminded,
}: { team: HrOverviewTeam } & Reminded): ReactElement => {
  const { t, i18n } = useTranslation();
  const remind = useRemindToSurveyMutation();
  const leaders = leadersText(team, t);
  return (
    <li className={classes.listRow}>
      <div className={classes.rowText}>
        <span className="ds-label">{team.name}</span>
        <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
          {t('hrOverview.surveysDue.team', {
            leaders,
            next: surveyDueText(team, t, i18n.language),
          })}
        </span>
      </div>
      {team.leaders.length > 0 && reminded[team.id] && (
        <Sent text={reminded[team.id]} />
      )}
      {team.leaders.length > 0 && !reminded[team.id] && (
        <div className={classes.rowText}>
          <Button
            variant="secondary"
            data-size="sm"
            loading={remind.isPending}
            onClick={() =>
              remind.mutate([team.id], {
                onSuccess: ({ sendsAt }) =>
                  onReminded([team.id], deliveryText(sendsAt, t, i18n.language)),
              })
            }
          >
            {t('hrOverview.surveysDue.remind', { leaders })}
          </Button>
          {remind.isError && (
            <ValidationMessage data-size="sm">
              {reminderErrorText(remind.error, t)}
            </ValidationMessage>
          )}
        </div>
      )}
    </li>
  );
};

const RemindAll = ({
  teams,
  onReminded,
}: {
  teams: HrOverviewTeam[];
  onReminded: Reminded['onReminded'];
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const remind = useRemindToSurveyMutation();
  const teamIds = teams.map((team) => team.id);
  if (teamIds.length === 0)
    return (
      <Paragraph data-size="sm" className={classes.done}>
        {t('hrOverview.surveysDue.allSent')}
      </Paragraph>
    );
  return (
    <div className={classes.rowText}>
      <Button
        className={classes.start}
        loading={remind.isPending}
        onClick={() =>
          remind.mutate(teamIds, {
            onSuccess: ({ sendsAt }) =>
              onReminded(teamIds, deliveryText(sendsAt, t, i18n.language)),
          })
        }
      >
        {t('hrOverview.surveysDue.remindAll', { count: teamIds.length })}
      </Button>
      {remind.isError && (
        <ValidationMessage data-size="sm">
          {reminderErrorText(remind.error, t)}
        </ValidationMessage>
      )}
    </div>
  );
};

const SurveysDue = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t } = useTranslation();
  const [reminded, setReminded] = useState<Record<number, string>>({});
  const onReminded = (teamIds: number[], text: string) =>
    setReminded((before) => ({
      ...before,
      ...Object.fromEntries(teamIds.map((teamId) => [teamId, text])),
    }));
  const groups = surveysDue(overview.teams).filter(
    (group) => group.teams.length > 0,
  );
  if (groups.length === 0)
    return <Paragraph>{t('hrOverview.fix.surveys.ok')}</Paragraph>;
  const toRemind = groups
    .flatMap((group) => group.teams)
    .filter((team) => team.leaders.length > 0 && !reminded[team.id]);
  return (
    <>
      <RemindAll teams={toRemind} onReminded={onReminded} />
      {groups.map(({ coverage, teams }) => (
        <section
          key={coverage}
          aria-labelledby={`due-${coverage}`}
          className={classes.card}
        >
          <div className={classes.heading}>
            <Heading level={2} data-size="xs" id={`due-${coverage}`}>
              {t(`hrOverview.surveysDue.groups.${coverage}.title`, {
                count: teams.length,
              })}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t(`hrOverview.surveysDue.groups.${coverage}.intro`, {
                months: overview.setup.intervalMonths,
              })}
            </Paragraph>
          </div>
          <ul className={classes.rows}>
            {teams.map((team) => (
              <TeamRow
                key={team.id}
                team={team}
                reminded={reminded}
                onReminded={onReminded}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
};

export const HRSurveysDue = (): ReactElement => (
  <OverviewSubpage
    page="surveys"
    values={(overview) => ({ months: overview.setup.intervalMonths })}
  >
    {(overview) => <SurveysDue overview={overview} />}
  </OverviewSubpage>
);
